import {useState, useEffect, MutableRefObject} from 'react'
import {useNavigate} from 'react-router-dom'
import {Tabs} from 'antd'
import dayjs from 'dayjs'
import styles from './NewRequest.module.scss'
import useStore from 'base/store'
// import {useGetProfile} from 'modules/profile'
import {useMount} from 'common/hooks'
import {useForm, useWatch} from 'common/components'
import {formatDate} from 'common/helpers'
import {FileInterface} from 'common/types'
import RequestAboutForm from './RequestAboutForm'
import RequestLoanForm from './RequestLoanForm'
import RequestDocumentsForm from './RequestDocumentsForm'
import {useCreateRequest, NewRequestValues, TNewRequestTab} from '..'

interface NewRequestProps {
  modalWrapRef: MutableRefObject<any>
  onClose: () => void
}

interface ITab {
  key: TNewRequestTab
  label: string
}

export default function NewRequest({modalWrapRef, onClose}: NewRequestProps) {
  const navigate = useNavigate()
  const role = useStore((state) => state.role)
  
  // const {data: profile} = useGetProfile({enabled: role['associated-manager']})
  const clearNewRequest = () => setNewRequest({})
  const {mutate: createRequest, isMutating} = useCreateRequest((newRequest) => {
    onClose()
    clearNewRequest()
    navigate(
      role.user || role.broker
        ? `/projects/${newRequest.project.uuid}`
        : `/requests/loan/${newRequest.uuid}`
    )
  })
  const newRequest = useStore((state) => state.newRequest)
  const setNewRequest = useStore((state) => state.setNewRequest)
  const [activeTab, setActiveTab] = useState<TNewRequestTab>(tabs[0].key)
  const [clickedTab, setClickedTab] = useState<TNewRequestTab>()
  const [aboutForm] = useForm()
  const [loanForm] = useForm()
  const isMounted = useMount()

  // console.log(newRequest)

  useEffect(() => {
    if (!isMounted) return
    modalWrapRef.current.scrollTop = 0
  }, [activeTab]) // eslint-disable-line

  const handleRequestCreate = () => {
    const {
      aboutFormSubmit,
      loanFormSubmit,
      brokerHelp,
      brokerName, brokerEmail, brokerPhoneNumber,
      borrowerName, borrowerEmail, borrowerPhoneNumber, borrowerBackground,
      dates,
      loanValueRequested,
      image,
      
      resourceConsents,
      engineeringPlanApprovals,
      buildingConsents,
      feasibility,
      // lendingManager,
      
      ...newRequestRest
    } = newRequest
    const newRequestToSend: NewRequestValues = {
      loanValueRequested,
      openDate: formatDate(dates[0], 'server'),
      maturityDate: formatDate(dates[1], 'server'),

      borrowerName, borrowerEmail, borrowerPhoneNumber, borrowerBackground,
      brokerName, brokerEmail, brokerPhoneNumber,
      
      newProject: {
        ...newRequestRest,
        image: image ? image.uuid : undefined,
        resourceConsents: getFilesUuids(resourceConsents),
        engineeringPlanApprovals: getFilesUuids(engineeringPlanApprovals),
        buildingConsents: getFilesUuids(buildingConsents),
        feasibility: getFilesUuids(feasibility),
      },
    }
    // console.log(newRequestToSend)
    
    // Todo: to include borrowerEmail & borrowerName
    
    // if (role['associated-manager']) {
    //   newRequestToSend.newProject.lendingManager =
    //     lendingManager ?? profile.managedUsers[0].uuid
    // } else if (role.user && (brokerName || brokerEmail)) {
    //   newRequestToSend.newProject.persons = [
    //     {
    //       role: 'broker',
    //       name: brokerName,
    //       email: brokerEmail,
    //     },
    //   ]
    // } else if (role.broker) {
    //   newRequestToSend.newProject.persons = [
    //     {
    //       role: 'user',
    //       name: borrowerName,
    //       email: borrowerEmail,
    //     },
    //   ]
    // }
    createRequest(newRequestToSend)
  }
  const imgVal = useWatch('image', aboutForm)
  const ttlVal = useWatch('title', aboutForm)
  const quickPass = () => {
    const title = `[By Lotus] ${ttlVal || ''} @${dayjs().format('D MMM')}`;
    const loanValueRequested = 10000000;
    const openDate = dayjs().add(10,'day').format('YYYY-MM-DD');
    const maturityDate = dayjs().add(10, 'month').format('YYYY-MM-DD');
    const image = imgVal?.uuid;
    createRequest({
      loanValueRequested, openDate, maturityDate,
      "borrowerBackground": "background of borrower",
      "newProject": {
        title, image,
        "units": 7,
        "address": "Address of Project",
        "description": "Details of project",
        "categories": [ "construction" ],
        "resourceConsents": [],
        "engineeringPlanApprovals": [],
        "buildingConsents": [],
        "feasibility": []
      },
      // borrowerName: , borrowerEmail: , borrowerPhoneNumber: , brokerName: , brokerEmail: , brokerPhoneNumber: ,
    })
  }

  const handleTabChange = (key: TNewRequestTab) => {
    if (activeTab === 'documents') {
      return setActiveTab(key)
    }
    activeTab === 'about' ? aboutForm.submit() : loanForm.submit()
  }

  const items = tabs.map((tab) => {
    const isDocumentsTab = tab.key === 'documents'
    const FormComponent = TabFormComponent[tab.key]
    return {
      ...tab,
      children: (
        <FormComponent
          form={
            tab.key === 'about'
              ? aboutForm
              : tab.key === 'loan'
              ? loanForm
              : undefined
          }
          clickedTab={isDocumentsTab ? undefined : clickedTab}
          newRequest={newRequest}
          isRequestCreating={isMutating}
          setNewRequest={setNewRequest}
          setTab={setActiveTab}
          setClickedTab={setClickedTab}
          onRequestCreate={isDocumentsTab ? handleRequestCreate : undefined}
          onClose={onClose}
        />
      ),
    }
  })

  return (
    <>
    { (process.env.NODE_ENV === 'development') &&
      <button onClick={quickPass}>Quick Pass</button>
    }
    <Tabs
      destroyInactiveTabPane
      className={styles.tabs}
      activeKey={activeTab}
      items={items}
      onChange={handleTabChange as (activeKey: string) => void}
      onTabClick={setClickedTab as (activeKey: string) => void}
    />
    </>
  )
}

const TabFormComponent = {
  about: RequestAboutForm,
  loan: RequestLoanForm,
  documents: RequestDocumentsForm,
}

const getFilesUuids = (files: FileInterface[]) =>
  files ? files.map((file: FileInterface) => file.uuid) : []

const tabs: ITab[] = [
  {key: 'about', label: 'About'},
  {key: 'loan', label: 'Loan'},
  {key: 'documents', label: 'Documents'},
]
